import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  ChevronLeft, 
  TrendingUp, 
  Calendar,
  FileText,
  Bell,
  ChevronRight,
  ExternalLink,
  Clock,
  CheckCircle,
  AlertCircle,
  Building2,
  Euro,
  Users,
  Percent
} from "lucide-react";
import { COLORS } from "./InvestOverviewPage";

// ═══════════════════════════════════════════════════════════
// MOCK INVESTOR DATA
// ═══════════════════════════════════════════════════════════
const MOCK_INVESTMENTS = [
  {
    id: "inv-001",
    cityId: "koeln",
    cityName: "Köln Ehrenfeld",
    levelName: "Growth Founder",
    levelEmoji: "🌳",
    amount: 500,
    color: "#059669",
    status: "funding", // funding, construction, open, active
    investedDate: "2024-03-15",
    expectedOpening: "Q2 2025",
    progress: 63,
    updates: 3,
  },
];

const MOCK_UPDATES = [
  {
    id: "upd-003",
    date: "2024-03-20",
    title: "Mietvertrag unterschrieben!",
    content: "Der Mietvertrag für den Standort in Köln Ehrenfeld wurde heute offiziell unterschrieben. Der Umbau kann beginnen!",
    type: "milestone",
  },
  {
    id: "upd-002",
    date: "2024-03-10",
    title: "50% Funding erreicht",
    content: "Wir haben die Hälfte unseres Funding-Ziels erreicht! Danke an alle bisherigen Investor:innen.",
    type: "progress",
  },
  {
    id: "upd-001",
    date: "2024-03-01",
    title: "Funding gestartet",
    content: "Das Crowdinvesting für Franky's Köln Ehrenfeld ist offiziell gestartet.",
    type: "info",
  },
];

// ═══════════════════════════════════════════════════════════
// STATUS CONFIG
// ═══════════════════════════════════════════════════════════
const STATUS_CONFIG = {
  funding: { 
    label: "Funding läuft", 
    color: COLORS.gold,
    icon: Clock,
  },
  construction: { 
    label: "Im Aufbau", 
    color: COLORS.primary,
    icon: Building2,
  },
  open: { 
    label: "Eröffnet", 
    color: COLORS.primary,
    icon: CheckCircle,
  },
  active: { 
    label: "Aktiv", 
    color: COLORS.primary,
    icon: TrendingUp,
  },
};

// ═══════════════════════════════════════════════════════════
// INVESTMENT CARD
// ═══════════════════════════════════════════════════════════
const InvestmentCard = ({ investment, onClick }) => {
  const status = STATUS_CONFIG[investment.status];
  const StatusIcon = status.icon;

  return (
    <motion.div
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="p-5 rounded-2xl cursor-pointer"
      style={{ 
        backgroundColor: COLORS.bgCard,
        border: `1px solid ${COLORS.border}`,
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center text-xl"
          style={{ backgroundColor: `${investment.color}20` }}
        >
          {investment.levelEmoji}
        </div>
        <div className="flex-1">
          <p className="font-bold" style={{ color: COLORS.textPrimary }}>
            {investment.cityName}
          </p>
          <p className="text-sm" style={{ color: COLORS.textMuted }}>
            {investment.levelName}
          </p>
        </div>
        <div className="text-right">
          <p className="font-bold" style={{ color: investment.color }}>
            {investment.amount.toLocaleString('de-DE')}€
          </p>
          <p className="text-xs" style={{ color: COLORS.textMuted }}>investiert</p>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span style={{ color: COLORS.textMuted }}>Funding-Fortschritt</span>
          <span style={{ color: COLORS.textPrimary }}>{investment.progress}%</span>
        </div>
        <div 
          className="h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: COLORS.bgCardLight }}
        >
          <div 
            className="h-full rounded-full"
            style={{ width: `${investment.progress}%`, backgroundColor: COLORS.primary }}
          />
        </div>
      </div>

      {/* Status & Info */}
      <div className="flex items-center justify-between pt-3" style={{ borderTop: `1px solid ${COLORS.border}` }}>
        <div className="flex items-center gap-2">
          <StatusIcon className="w-4 h-4" style={{ color: status.color }} />
          <span 
            className="text-sm font-medium"
            style={{ color: status.color }}
          >
            {status.label}
          </span>
        </div>
        <div className="flex items-center gap-1 text-sm" style={{ color: COLORS.textMuted }}>
          <Calendar className="w-4 h-4" />
          <span>{investment.expectedOpening}</span>
        </div>
      </div>

      {/* Updates Badge */}
      {investment.updates > 0 && (
        <div className="mt-3 flex items-center gap-2">
          <Bell className="w-4 h-4" style={{ color: COLORS.gold }} />
          <span className="text-sm" style={{ color: COLORS.gold }}>
            {investment.updates} neue Updates
          </span>
        </div>
      )}
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════
// UPDATE CARD
// ═══════════════════════════════════════════════════════════
const UpdateCard = ({ update }) => {
  const typeConfig = {
    milestone: { color: COLORS.primary, icon: CheckCircle },
    progress: { color: COLORS.gold, icon: TrendingUp },
    info: { color: COLORS.textMuted, icon: FileText },
  };
  const config = typeConfig[update.type] || typeConfig.info;
  const Icon = config.icon;

  return (
    <div 
      className="p-4 rounded-xl"
      style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}
    >
      <div className="flex items-start gap-3">
        <div 
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${config.color}20` }}
        >
          <Icon className="w-4 h-4" style={{ color: config.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <p className="font-semibold text-sm" style={{ color: COLORS.textPrimary }}>
              {update.title}
            </p>
            <span className="text-xs" style={{ color: COLORS.textMuted }}>
              {new Date(update.date).toLocaleDateString('de-DE')}
            </span>
          </div>
          <p className="text-sm" style={{ color: COLORS.textSecondary }}>
            {update.content}
          </p>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN INVESTOR DASHBOARD
// ═══════════════════════════════════════════════════════════
export default function InvestorDashboard() {
  const navigate = useNavigate();
  const { user, setShowAuthModal } = useAuth();
  const [activeTab, setActiveTab] = useState("investments"); // investments, updates

  // Check if user is logged in
  if (!user) {
    return (
      <div 
        className="min-h-screen flex flex-col items-center justify-center px-6"
        style={{ backgroundColor: COLORS.bgDark }}
      >
        <div className="text-center">
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${COLORS.primary}20` }}
          >
            <TrendingUp className="w-10 h-10" style={{ color: COLORS.primary }} />
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: COLORS.textPrimary }}>
            Investor Dashboard
          </h2>
          <p className="text-sm mb-6" style={{ color: COLORS.textMuted }}>
            Melde dich an, um deine Investments zu verwalten
          </p>
          <button
            onClick={() => setShowAuthModal(true)}
            className="px-6 py-3 rounded-xl font-bold"
            style={{ backgroundColor: COLORS.primary, color: COLORS.white }}
          >
            Jetzt anmelden
          </button>
        </div>
      </div>
    );
  }

  const totalInvested = MOCK_INVESTMENTS.reduce((sum, inv) => sum + inv.amount, 0);
  const hasInvestments = MOCK_INVESTMENTS.length > 0;

  return (
    <div 
      className="min-h-screen pb-8"
      style={{ backgroundColor: COLORS.bgDark }}
    >
      {/* Header */}
      <header className="px-4 pt-6 pb-4 flex items-center gap-3">
        <motion.button 
          whileTap={{ scale: 0.9 }} 
          onClick={() => navigate("/invest")}
          className="p-2 rounded-xl"
          style={{ backgroundColor: COLORS.bgCardLight }}
        >
          <ChevronLeft className="w-5 h-5" style={{ color: COLORS.textPrimary }} />
        </motion.button>
        <div>
          <h1 className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
            Investor Dashboard
          </h1>
          <p className="text-xs" style={{ color: COLORS.textMuted }}>
            Deine Investments verwalten
          </p>
        </div>
      </header>

      {hasInvestments ? (
        <>
          {/* Portfolio Summary */}
          <section className="px-5 py-4">
            <div 
              className="p-5 rounded-2xl"
              style={{ 
                background: `linear-gradient(135deg, ${COLORS.bgCard} 0%, ${COLORS.bgAccent} 100%)`,
                border: `1px solid ${COLORS.primary}40`
              }}
            >
              <p className="text-sm mb-1" style={{ color: COLORS.textMuted }}>Gesamtportfolio</p>
              <p className="text-3xl font-bold mb-4" style={{ color: COLORS.textPrimary }}>
                {totalInvested.toLocaleString('de-DE')}€
              </p>

              <div className="grid grid-cols-3 gap-3">
                <div className="text-center">
                  <p className="text-lg font-bold" style={{ color: COLORS.primary }}>
                    {MOCK_INVESTMENTS.length}
                  </p>
                  <p className="text-xs" style={{ color: COLORS.textMuted }}>Investments</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold" style={{ color: COLORS.gold }}>
                    20%
                  </p>
                  <p className="text-xs" style={{ color: COLORS.textMuted }}>Gewinnanteil</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
                    Q1 2026
                  </p>
                  <p className="text-xs" style={{ color: COLORS.textMuted }}>Erste Auszahlung</p>
                </div>
              </div>
            </div>
          </section>

          {/* Tabs */}
          <section className="px-5 py-2">
            <div 
              className="flex p-1 rounded-xl"
              style={{ backgroundColor: COLORS.bgCard }}
            >
              <button
                onClick={() => setActiveTab("investments")}
                className="flex-1 py-2 rounded-lg text-sm font-medium transition-all"
                style={{
                  backgroundColor: activeTab === "investments" ? COLORS.primary : 'transparent',
                  color: activeTab === "investments" ? COLORS.white : COLORS.textSecondary
                }}
              >
                Investments
              </button>
              <button
                onClick={() => setActiveTab("updates")}
                className="flex-1 py-2 rounded-lg text-sm font-medium transition-all relative"
                style={{
                  backgroundColor: activeTab === "updates" ? COLORS.primary : 'transparent',
                  color: activeTab === "updates" ? COLORS.white : COLORS.textSecondary
                }}
              >
                Updates
                {MOCK_UPDATES.length > 0 && (
                  <span 
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold"
                    style={{ backgroundColor: COLORS.gold, color: COLORS.bgDark }}
                  >
                    {MOCK_UPDATES.length}
                  </span>
                )}
              </button>
            </div>
          </section>

          {/* Content */}
          <section className="px-5 py-4">
            {activeTab === "investments" ? (
              <div className="space-y-4">
                {MOCK_INVESTMENTS.map((investment) => (
                  <InvestmentCard
                    key={investment.id}
                    investment={investment}
                    onClick={() => navigate(`/invest/city/${investment.cityId}`)}
                  />
                ))}

                {/* Add More CTA */}
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate("/invest")}
                  className="w-full p-4 rounded-xl flex items-center justify-center gap-2"
                  style={{ 
                    backgroundColor: COLORS.bgCard,
                    border: `1px dashed ${COLORS.border}`,
                  }}
                >
                  <span style={{ color: COLORS.textMuted }}>Weitere Investments entdecken</span>
                  <ChevronRight className="w-4 h-4" style={{ color: COLORS.textMuted }} />
                </motion.button>
              </div>
            ) : (
              <div className="space-y-3">
                {MOCK_UPDATES.map((update) => (
                  <UpdateCard key={update.id} update={update} />
                ))}
              </div>
            )}
          </section>

          {/* Documents Section */}
          <section className="px-5 py-4">
            <h3 className="text-sm font-semibold mb-3" style={{ color: COLORS.textMuted }}>
              DOKUMENTE
            </h3>
            <div className="space-y-2">
              {[
                { title: "Beteiligungsvertrag", date: "15.03.2024" },
                { title: "Investor-Information", date: "01.03.2024" },
                { title: "Jahresbericht 2023", date: "15.01.2024" },
              ].map((doc, i) => (
                <div
                  key={i}
                  className="p-3 rounded-xl flex items-center gap-3"
                  style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}
                >
                  <FileText className="w-5 h-5" style={{ color: COLORS.textMuted }} />
                  <div className="flex-1">
                    <p className="text-sm font-medium" style={{ color: COLORS.textPrimary }}>
                      {doc.title}
                    </p>
                    <p className="text-xs" style={{ color: COLORS.textMuted }}>{doc.date}</p>
                  </div>
                  <ExternalLink className="w-4 h-4" style={{ color: COLORS.primary }} />
                </div>
              ))}
            </div>
          </section>

          {/* Help Section */}
          <section className="px-5 py-4">
            <div 
              className="p-4 rounded-xl"
              style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}` }}
            >
              <p className="font-semibold text-sm mb-1" style={{ color: COLORS.textPrimary }}>
                Fragen zu deinem Investment?
              </p>
              <p className="text-sm mb-3" style={{ color: COLORS.textMuted }}>
                Unser Investor Relations Team ist für dich da.
              </p>
              <button 
                className="text-sm font-medium"
                style={{ color: COLORS.primary }}
              >
                investor@frankysfarm.de →
              </button>
            </div>
          </section>
        </>
      ) : (
        /* Empty State */
        <section className="px-5 py-12 text-center">
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: COLORS.bgCard }}
          >
            <TrendingUp className="w-10 h-10" style={{ color: COLORS.textMuted }} />
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: COLORS.textPrimary }}>
            Noch keine Investments
          </h2>
          <p className="text-sm mb-6" style={{ color: COLORS.textMuted }}>
            Werde Teil von Franky's Farm und investiere in eine unserer Filialen.
          </p>
          <button
            onClick={() => navigate("/invest")}
            className="px-6 py-3 rounded-xl font-bold"
            style={{ backgroundColor: COLORS.primary, color: COLORS.white }}
          >
            Investments entdecken
          </button>
        </section>
      )}
    </div>
  );
}
